import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Grid, Container, Paper, TextField, MenuItem,
  Button, useTheme, alpha, Avatar, Chip, Divider, FormControl,
  FormLabel, RadioGroup, FormControlLabel, Radio, InputLabel,
  Select, OutlinedInput, Checkbox, ListItemText, Card, CardContent,
  SelectChangeEvent, Snackbar, Alert, CircularProgress, Dialog,
  DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  AccountCircle as AccountCircleIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Work as WorkIcon,
  ArrowForward as ArrowForwardIcon,
  CameraAlt as CameraAltIcon,
  SaveAlt as SaveAltIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { register as apiRegister, verifyOTP as apiVerifyOTP, resendOTP as apiResendOTP, completeRegistration as apiCompleteRegistration, type RegisterPayload, verifyInvite as apiVerifyInvite, registerWithInvite as apiRegisterWithInvite, type RegisterWithInvitePayload } from "../services/auth";
import { uploadProfilePhoto } from '../services/upload';

// Mock data for sectors (would likely come from your API or data file)
const sectors = [
  'Food Processing',
  'Life Sciences & Pharma',
  'Biotechnology',
  'Automotive',
  'Renewables',
  'Machinery & Equipment',
  'Medical Devices & Technology',
  'Minerals & Metals',
  'Chemicals',
  'Building Materials',
  'Aerospace',
  'Maritime',
  'Military',
  'Robotics',
  'Petroleum',
  'Petrochemicals',
  'Mining',
  'Metal Goods',
  'Packaging',
  'Dairy',
  'Dates',
  'Retail & Wholesale',
  'HoReCa',
  'IT, Data & AI',
  'Finance & Banking',
  'Supply Chain & Logistics',
  'Construction & Development',
  'Healthcare Sector',
  'Tourism Sector',
  'Professional Education',
  'Creative Industries',
  'Hospitality Sector',
  'Sport & Lifestyle',
  'Media & Entertainment',
  'R&D & Innovation'
];

// Organization segments
const segments = [
  'Government',
  'Investment Institution',
  'Private Sector Company',
  'RDI',
  'University',
  'Startup',
  'Solo-entrepreneur'
];

const RegisterPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Invitation state
  const [isInviteRegistration, setIsInviteRegistration] = useState(false);
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState<string | null>(null);
  const [inviteUserType, setInviteUserType] = useState<"member" | "expert" | null>(null);
  const [verifyingInvite, setVerifyingInvite] = useState(false);

  // Form state
  const [formData, setFormData] = useState<{
    userType: "member" | "expert";
    firstName: string;
    lastName: string;
    title: string;
    experience: string;
    organizationName: string;
    organizationBrief: string;
    segment: string;
    country: string;
    selectedSectors: string[];
    email: string;
    password: string;
    confirmPassword: string;
    photoUrl: string;
  }>({
    userType: 'member',
    firstName: '',
    lastName: '',
    title: '',
    experience: '',
    organizationName: '',
    organizationBrief: '',
    segment: '',
    country: '',
    selectedSectors: [],
    email: '',
    password: '',
    confirmPassword: '',
    photoUrl: '',
  });

  // Photo upload state
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);

  // Form section visibility state
  const [activeSection, setActiveSection] = useState('personal');

  // Loading and error state
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // OTP verification state
  const [showOTPDialog, setShowOTPDialog] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpExpiry, setOtpExpiry] = useState(10); // minutes
  const [resendCooldown, setResendCooldown] = useState(0);
  const [emailVerified, setEmailVerified] = useState(false);
  const [emailVerifiedAt, setEmailVerifiedAt] = useState<number | null>(null); // timestamp when email was verified
  const [sendingOTP, setSendingOTP] = useState(false);

  // Check for invitation parameters on mount
  useEffect(() => {
    const token = searchParams.get('token');
    const email = searchParams.get('email');

    if (token && email) {
      setVerifyingInvite(true);
      apiVerifyInvite(token)
        .then((response) => {
          const { email: verifiedEmail, userType } = response.data;
          setIsInviteRegistration(true);
          setInviteToken(token);
          setInviteEmail(verifiedEmail);
          setInviteUserType(userType);
          setFormData(prev => ({
            ...prev,
            email: verifiedEmail,
            userType: userType
          }));
          setEmailVerified(true); // Skip OTP verification
          setSnackbar({
            open: true,
            message: "You've been invited to join Value Chain Global!",
            severity: 'success'
          });
        })
        .catch((error) => {
          console.error('Invalid or expired invitation:', error);
          setSnackbar({
            open: true,
            message: 'Invalid or expired invitation link',
            severity: 'error'
          });
        })
        .finally(() => {
          setVerifyingInvite(false);
        });
    }
  }, [searchParams]);

  // Handle regular input changes (for TextField components)
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // If photoUrl is being updated, update the preview
    if (name === 'photoUrl' && value) {
      setPhotoPreview(value);
      setPhoto(null); // Clear file upload if URL is provided
    }
  };

  // Handle Select component changes
  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name!]: value }));
  };

  // Handle multi-select sector changes
  const handleSectorChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      selectedSectors: typeof value === 'string' ? value.split(',') : value
    }));
  };

  const handlePhotoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];
      setPhoto(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target && e.target.result) {
          const dataUrl = e.target.result as string;
          setPhotoPreview(dataUrl);
          // Clear the photoUrl field when file is uploaded
          setFormData((prev) => ({ ...prev, photoUrl: '' }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSendOTP = async () => {
    if (!formData.email) {
      setSnackbar({
        open: true,
        message: 'Please enter your email address',
        severity: 'error'
      });
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid email address',
        severity: 'error'
      });
      return;
    }

    // Only email is required for OTP verification
    // User will fill complete form after email verification

    try {
      setSendingOTP(true);

      // Only send email for OTP verification
      const payload = {
        email: formData.email
      };

      console.log('Sending OTP to email:', formData.email);
      const response = await apiRegister(payload as any);

      if (response.data?.requiresOTP) {
        setOtpEmail(formData.email);
        setOtpExpiry(response.data.expiresIn || 10);
        setSnackbar({
          open: true,
          message: response.data.message || 'Verification code sent to your email',
          severity: 'success'
        });
      }
    } catch (e: any) {
      console.error('Send OTP error:', e);

      // Handle specific error codes
      const errorCode = e?.response?.data?.error?.code;
      const errorMessage = e?.response?.data?.error?.message;
      const errorDetails = e?.response?.data?.error?.details;

      let displayMessage = errorMessage || 'Failed to send verification code. Please check your email address.';

      // Special handling for registrations disabled
      if (errorCode === 'REGISTRATIONS_DISABLED') {
        displayMessage = errorDetails?.message || errorMessage || 'New user registrations are currently disabled.';
      }

      setSnackbar({
        open: true,
        message: displayMessage,
        severity: 'error'
      });
    } finally {
      setSendingOTP(false);
    }
  };

  const handleVerifyOTPInline = async () => {
    if (!otp || otp.length !== 6) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid 6-digit code',
        severity: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      // Verify OTP with backend
      await apiVerifyOTP(otpEmail, otp);

      // Mark as verified and save timestamp
      setEmailVerified(true);
      setEmailVerifiedAt(Date.now());
      setOtp('');
      setSnackbar({
        open: true,
        message: 'Email verified successfully! Please complete the rest of the form.',
        severity: 'success'
      });
    } catch (e: any) {
      console.error('OTP verification error:', e);
      const errorMessage = e?.response?.data?.error?.message || 'Invalid verification code';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setSnackbar({
        open: true,
        message: 'Please enter a valid 6-digit code',
        severity: 'error'
      });
      return;
    }

    try {
      setLoading(true);
      await apiVerifyOTP(otpEmail, otp);

      setSnackbar({
        open: true,
        message: 'Email verified successfully! Your account is pending admin approval.',
        severity: 'success'
      });

      setShowOTPDialog(false);
      setTimeout(() => {
        navigate('/login?verified=true');
      }, 2000);
    } catch (e: any) {
      console.error('OTP verification error:', e);
      const errorMessage = e?.response?.data?.error?.message || 'Invalid verification code';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) {
      return;
    }

    try {
      setLoading(true);
      const response = await apiResendOTP(otpEmail);

      setSnackbar({
        open: true,
        message: response.data?.message || 'New verification code sent',
        severity: 'success'
      });

      setOtp('');
      setResendCooldown(60); // 60 second cooldown

      // Countdown timer
      const interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (e: any) {
      console.error('Resend OTP error:', e);
      const errorMessage = e?.response?.data?.error?.message || 'Failed to resend code';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }

    if (formData.password.length < 8) {
      setSnackbar({
        open: true,
        message: 'Password must be at least 8 characters long',
        severity: 'error'
      });
      return;
    }

    if (formData.confirmPassword && formData.confirmPassword !== formData.password) {
      setSnackbar({
        open: true,
        message: 'Passwords do not match',
        severity: 'error'
      });
      return;
    }

    if (formData.selectedSectors.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one sector',
        severity: 'error'
      });
      return;
    }

    try {
      setLoading(true);

      // Upload photo to Cloudinary if a file was selected (optional)
      let uploadedPhotoUrl = formData.photoUrl;
      if (photo) {
        try {
          const uploadResult = await uploadProfilePhoto(photo);
          uploadedPhotoUrl = uploadResult.url;
        } catch (error) {
          console.error('Error uploading photo:', error);
          // Photo upload is optional, so we continue without it
          uploadedPhotoUrl = '';
        }
      }

      // Handle invitation-based registration
      if (isInviteRegistration && inviteToken) {
        const invitePayload: RegisterWithInvitePayload = {
          token: inviteToken,
          firstName: formData.firstName,
          lastName: formData.lastName,
          title: formData.title,
          experience: formData.experience,
          organizationName: formData.organizationName,
          organizationBrief: formData.organizationBrief,
          segment: formData.segment,
          country: formData.country,
          selectedSectors: formData.selectedSectors,
          password: formData.password,
          photoUrl: uploadedPhotoUrl || undefined,
        };

        console.log('🎟️ RegisterPage - Invite registration payload:', invitePayload);

        const response = await apiRegisterWithInvite(invitePayload);

        // Show success and redirect to appropriate dashboard
        setSnackbar({
          open: true,
          message: 'Registration successful! Redirecting to your dashboard...',
          severity: 'success'
        });

        setTimeout(() => {
          // Redirect based on user type
          if (response.data?.user?.userType === 'expert') {
            navigate('/expert-dashboard');
          } else if (response.data?.user?.userType === 'member') {
            navigate('/member-dashboard');
          } else {
            navigate('/');
          }
        }, 2000);

        return;
      }

      // Build payload that matches services/auth.ts for regular registration
      const payload: RegisterPayload = {
        userType: formData.userType,
        firstName: formData.firstName,
        lastName: formData.lastName,
        title: formData.title,
        experience: formData.experience,
        organizationName: formData.organizationName,
        organizationBrief: formData.organizationBrief,
        segment: formData.segment,
        country: formData.country,
        selectedSectors: formData.selectedSectors,
        email: formData.email,
        password: formData.password,
        photoUrl: uploadedPhotoUrl || undefined,
      };

      console.log('🔍 RegisterPage - formData.userType:', formData.userType);
      console.log('🔍 RegisterPage - payload before sending:', payload);
      console.log('🔍 RegisterPage - payload.userType:', payload.userType);

      // Check if email was verified within 30 minutes
      const thirtyMinutesInMs = 30 * 60 * 1000;
      const isVerificationValid = emailVerified &&
                                  emailVerifiedAt &&
                                  (Date.now() - emailVerifiedAt) < thirtyMinutesInMs;

      if (isVerificationValid) {
        // Email already verified - complete registration directly
        console.log('✅ Email already verified - completing registration');
        const response = await apiCompleteRegistration(payload);

        setSnackbar({
          open: true,
          message: response.data?.message || 'Registration successful! Your account is pending admin approval.',
          severity: 'success'
        });
        setTimeout(() => {
          navigate('/login?registered=true');
        }, 2000);
      } else {
        // Email not verified yet - send OTP first
        console.log('📧 Email not verified - sending OTP');
        if (emailVerified && !isVerificationValid) {
          // Verification expired
          setEmailVerified(false);
          setEmailVerifiedAt(null);
        }

        // Need to show error asking user to verify email first
        setSnackbar({
          open: true,
          message: 'Please verify your email address before completing registration.',
          severity: 'warning'
        });
      }
    } catch (e: any) {
      console.error('Registration error:', e);
      const errorMessage = e?.response?.data?.error?.message || 'Registration failed. Please try again.';
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* Page Header with gradient background */}
      <Paper
        elevation={0}
        sx={{
          mb: 6,
          p: { xs: 3, md: 5 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.1)})`,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            opacity: 0.1,
            background: `radial-gradient(circle, ${theme.palette.secondary.main} 0%, transparent 70%)`,
            transform: 'translateX(30%)'
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            Register
          </Typography>

          <Typography
            variant="subtitle1"
            color="text.secondary"
            sx={{
              maxWidth: '800px',
              lineHeight: 1.6,
              mb: 2
            }}
          >
            Join our platform to connect with industry professionals, access specialized value chains,
            and explore investment opportunities in the healthcare and life sciences sector.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            <Chip
              label="Industry Network"
              color="secondary"
              size="medium"
              sx={{ borderRadius: 1, fontWeight: 500 }}
            />
            <Chip
              label="Knowledge Exchange"
              variant="outlined"
              size="medium"
              sx={{ borderRadius: 1, fontWeight: 500 }}
            />
            <Chip
              label="Investment Opportunities"
              variant="outlined"
              size="medium"
              sx={{ borderRadius: 1, fontWeight: 500 }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Registration form */}
      <Paper
        elevation={3}
        component="form"
        onSubmit={handleSubmit}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          mb: 6
        }}
      >
        {/* Invitation Banner */}
        {isInviteRegistration && (
          <Box
            sx={{
              p: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.light, 0.05)})`,
              borderBottom: `3px solid ${theme.palette.success.main}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: theme.palette.success.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`
              }}
            >
              <PersonAddIcon sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} color="success.dark" sx={{ mb: 0.5 }}>
                You've Been Invited! 🎉
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Complete your profile to join Value Chain Global{inviteUserType ? ` as a ${inviteUserType}` : ''}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Form header */}
        <Box
          sx={{
            p: 3,
            background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
            color: 'white'
          }}
        >
          <Typography variant="h4" fontWeight={600}>
            Registration Form
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
            {isInviteRegistration
              ? 'Complete your profile to join the platform'
              : 'Complete the form below to create your account'}
          </Typography>
        </Box>

        {/* Progress Stepper */}
        <Box
          sx={{
            display: 'flex',
            borderBottom: `2px solid ${theme.palette.divider}`,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            position: 'relative'
          }}
        >
          {[
            { id: 'personal', label: 'Personal Info', icon: <AccountCircleIcon />, step: 1 },
            { id: 'organization', label: 'Organization', icon: <BusinessIcon />, step: 2, requiresVerification: true },
            { id: 'expertise', label: 'Expertise', icon: <WorkIcon />, step: 3, requiresVerification: true }
          ].map((section, index) => {
            const isActive = activeSection === section.id;
            const isCompleted =
              (section.id === 'personal' && emailVerified) ||
              (section.id === 'organization' && activeSection === 'expertise');
            const isDisabled = section.requiresVerification && !emailVerified;

            return (
              <Box
                key={section.id}
                sx={{
                  flex: 1,
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Button
                  startIcon={section.icon}
                  disabled={isDisabled}
                  sx={{
                    width: '100%',
                    py: 2.5,
                    px: 3,
                    borderRadius: 0,
                    borderBottom: isActive ?
                      `4px solid ${theme.palette.secondary.main}` :
                      '4px solid transparent',
                    color: isDisabled
                      ? theme.palette.text.disabled
                      : isActive
                        ? theme.palette.secondary.main
                        : isCompleted
                          ? theme.palette.success.main
                          : theme.palette.text.primary,
                    fontWeight: isActive ? 700 : 500,
                    backgroundColor: isActive
                      ? alpha(theme.palette.secondary.main, 0.08)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: isDisabled
                        ? 'transparent'
                        : alpha(theme.palette.secondary.main, 0.12)
                    },
                    cursor: isDisabled ? 'not-allowed' : 'default',
                    pointerEvents: 'none',
                    opacity: isDisabled ? 0.5 : 1,
                    transition: 'all 0.3s ease'
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={section.step}
                        size="small"
                        sx={{
                          backgroundColor: isCompleted
                            ? theme.palette.success.main
                            : isActive
                              ? theme.palette.secondary.main
                              : alpha(theme.palette.text.primary, 0.2),
                          color: isCompleted || isActive ? 'white' : theme.palette.text.secondary,
                          fontWeight: 700,
                          width: 28,
                          height: 28
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: isActive ? 700 : 500,
                          display: { xs: 'none', sm: 'block' }
                        }}
                      >
                        {section.label}
                      </Typography>
                    </Box>
                  </Box>
                </Button>
                {index < 2 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 0,
                      height: 0,
                      borderTop: '12px solid transparent',
                      borderBottom: '12px solid transparent',
                      borderLeft: `12px solid ${theme.palette.divider}`,
                      zIndex: 1
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>

        {/* Form content */}
        <Box sx={{ p: 4 }}>
          {/* Personal Information Section */}
          {activeSection === 'personal' && (
            <Box>
              <Typography
                variant="h5"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  position: 'relative',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: 40,
                    height: 3,
                    borderRadius: 1.5,
                    backgroundColor: theme.palette.secondary.main
                  }
                }}
              >
                Personal Information
              </Typography>

              <Grid container spacing={4}>
                {/* Member Type Selection */}
                <Grid item xs={12}>
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      mb: 2,
                      borderColor: alpha(theme.palette.primary.main, 0.3)
                    }}
                  >
                    <CardContent>
                      <Typography gutterBottom variant="h6" component="div" sx={{ fontWeight: 600 }}>
                        Account Type
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select your account type. Experts have additional visibility and can contribute specialized content.
                      </Typography>

                      <RadioGroup
                        row
                        name="userType"
                        value={formData.userType}
                        onChange={handleInputChange}
                      >
                        <FormControlLabel
                          value="member"
                          disabled={isInviteRegistration}
                          control={
                            <Radio
                              sx={{
                                '&.Mui-checked': {
                                  color: theme.palette.secondary.main
                                }
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body1" fontWeight={500}>Member</Typography>
                              <Chip
                                label="Standard"
                                size="small"
                                sx={{ ml: 1, backgroundColor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
                              />
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="expert"
                          disabled={isInviteRegistration}
                          control={
                            <Radio
                              sx={{
                                '&.Mui-checked': {
                                  color: theme.palette.secondary.main
                                }
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body1" fontWeight={500}>Expert</Typography>
                              <Chip
                                label="Verified"
                                size="small"
                                sx={{ ml: 1, backgroundColor: alpha(theme.palette.secondary.main, 0.1), color: theme.palette.secondary.main }}
                              />
                            </Box>
                          }
                        />
                      </RadioGroup>
                      {isInviteRegistration && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Account type pre-selected based on your invitation
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Profile Photo */}
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 3,
                      border: `1px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
                      borderRadius: 2,
                      backgroundColor: alpha(theme.palette.primary.main, 0.02),
                      minHeight: 250,
                      position: 'relative'
                    }}
                  >
                    {photoPreview ? (
                      <Box sx={{ position: 'relative' }}>
                        <Avatar
                          src={photoPreview}
                          alt="Profile Preview"
                          sx={{
                            width: 150,
                            height: 150,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`
                          }}
                        />
                        <Button
                          variant="contained"
                          size="small"
                          color="primary"
                          startIcon={<CameraAltIcon />}
                          component="label"
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            borderRadius: '50%',
                            minWidth: 'auto',
                            p: 1
                          }}
                        >
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handlePhotoChange}
                          />
                          <Box sx={{ width: 24, height: 24 }} />
                        </Button>
                      </Box>
                    ) : (
                      <>
                        <CloudUploadIcon
                          color="primary"
                          sx={{ fontSize: 60, mb: 2, opacity: 0.7 }}
                        />
                        <Typography
                          variant="subtitle1"
                          align="center"
                          gutterBottom
                          sx={{ fontWeight: 500 }}
                        >
                          Upload Profile Photo
                        </Typography>
                        <Typography
                          variant="body2"
                          align="center"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          Recommended size: 300x300 pixels
                        </Typography>
                        <Button
                          variant="outlined"
                          component="label"
                          startIcon={<CameraAltIcon />}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none'
                          }}
                        >
                          Select Photo
                          <input
                            type="file"
                            hidden
                            accept="image/*"
                            onChange={handlePhotoChange}
                          />
                        </Button>
                      </>
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: 'center',
                      mt: 1,
                      color: theme.palette.text.secondary
                    }}
                  >
                    Profile photo (optional) - Upload or provide URL below
                  </Typography>
                  <TextField
                    fullWidth
                    label="Photo URL (optional)"
                    name="photoUrl"
                    value={formData.photoUrl}
                    onChange={handleInputChange}
                    variant="outlined"
                    placeholder="https://example.com/photo.jpg"
                    helperText="Enter a URL to your profile photo or upload one above"
                    sx={{ mt: 2 }}
                  />
                </Grid>

                {/* Personal Information Form Fields */}
                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="First Name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Last Name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      {isInviteRegistration ? (
                        <TextField
                          required
                          fullWidth
                          label="Email"
                          name="email"
                          type="email"
                          value={formData.email}
                          disabled
                          variant="outlined"
                          helperText="Email pre-filled from invitation"
                          sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: alpha(theme.palette.success.main, 0.05)
                            }
                          }}
                        />
                      ) : (
                        <Paper
                          elevation={emailVerified ? 2 : 0}
                          variant={emailVerified ? "elevation" : "outlined"}
                          sx={{
                            p: 3,
                            mb: 2,
                            borderRadius: 2,
                            borderColor: emailVerified
                              ? theme.palette.success.main
                              : alpha(theme.palette.primary.main, 0.3),
                            borderWidth: 2,
                            backgroundColor: emailVerified
                              ? alpha(theme.palette.success.main, 0.05)
                              : alpha(theme.palette.background.paper, 0.5),
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              mb: 2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            Email Verification
                            {emailVerified && (
                              <Chip
                                label="✓ Verified"
                                color="success"
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            )}
                          </Typography>

                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                required
                                fullWidth
                                label="Company Email"
                                name="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => {
                                  handleInputChange(e);
                                  setEmailVerified(false);
                                  setEmailVerifiedAt(null);
                                  setOtpEmail('');
                                  setOtp('');
                                }}
                                variant="outlined"
                                helperText="Generic emails (Gmail, Yahoo, Hotmail) not allowed"
                                disabled={emailVerified}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: emailVerified
                                      ? alpha(theme.palette.success.main, 0.05)
                                      : 'transparent'
                                  }
                                }}
                              />
                            </Grid>

                          <Grid item xs={12} md={6}>
                            <Box sx={{ display: 'flex', gap: 1, height: '100%' }}>
                              <TextField
                                fullWidth
                                label="Verification Code"
                                value={otp}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                  setOtp(value);
                                }}
                                placeholder="000000"
                                disabled={emailVerified || !otpEmail}
                                inputProps={{
                                  maxLength: 6,
                                  style: {
                                    fontSize: '18px',
                                    letterSpacing: '4px',
                                    textAlign: 'center',
                                    fontWeight: 600
                                  }
                                }}
                                helperText={
                                  emailVerified
                                    ? "✓ Email verified"
                                    : otpEmail
                                      ? `Sent to ${otpEmail.substring(0, 3)}***`
                                      : "Enter email first"
                                }
                              />
                              {!emailVerified && (
                                <Button
                                  variant={otpEmail ? "outlined" : "contained"}
                                  color={otpEmail ? "primary" : "secondary"}
                                  onClick={otpEmail ? handleResendOTP : handleSendOTP}
                                  disabled={sendingOTP || loading || (resendCooldown > 0 && Boolean(otpEmail))}
                                  sx={{
                                    minWidth: 110,
                                    height: 56,
                                    textTransform: 'none',
                                    fontWeight: 600
                                  }}
                                >
                                  {sendingOTP ? (
                                    <CircularProgress size={20} />
                                  ) : otpEmail ? (
                                    resendCooldown > 0 ? `${resendCooldown}s` : 'Resend'
                                  ) : (
                                    'Send Code'
                                  )}
                                </Button>
                              )}
                              {otpEmail && !emailVerified && otp.length === 6 && (
                                <Button
                                  variant="contained"
                                  color="success"
                                  onClick={handleVerifyOTPInline}
                                  disabled={loading}
                                  sx={{
                                    minWidth: 100,
                                    height: 56,
                                    textTransform: 'none',
                                    fontWeight: 600
                                  }}
                                >
                                  {loading ? <CircularProgress size={20} color="inherit" /> : 'Verify'}
                                </Button>
                              )}
                            </Box>
                          </Grid>
                        </Grid>

                        {otpEmail && !emailVerified && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              mt: 2,
                              color: theme.palette.text.secondary,
                              fontStyle: 'italic'
                            }}
                          >
                            Check your email inbox for the verification code. Code expires in {otpExpiry} minutes.
                          </Typography>
                        )}
                      </Paper>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        fullWidth
                        label="Password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    {/* Optional confirm */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Confirm Password"
                        name="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        variant="outlined"
                        sx={{ mb: 2 }}
                        helperText={formData.confirmPassword && formData.confirmPassword !== formData.password ? "Passwords do not match" : " "}
                        error={!!formData.confirmPassword && formData.confirmPassword !== formData.password}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        label="Professional Title / Position"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        variant="outlined"
                        placeholder="e.g., Senior Researcher, Project Manager, CEO"
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        required
                        fullWidth
                        label="Professional Experience"
                        name="experience"
                        value={formData.experience}
                        onChange={handleInputChange}
                        variant="outlined"
                        multiline
                        rows={4}
                        placeholder="Briefly describe your professional background and expertise..."
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                <Button
                  variant="contained"
                  color="secondary"
                  endIcon={<ArrowForwardIcon />}
                  size="large"
                  onClick={() => {
                    if (!emailVerified && !isInviteRegistration) {
                      setSnackbar({
                        open: true,
                        message: 'Please verify your email before continuing',
                        severity: 'warning'
                      });
                      return;
                    }
                    setActiveSection('organization');
                  }}
                  disabled={!emailVerified && !isInviteRegistration}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    px: 3,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Continue to Organization
                </Button>
              </Box>
            </Box>
          )}

          {/* Organization Section */}
          {activeSection === 'organization' && (
            <Box>
              <Typography
                variant="h5"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  position: 'relative',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: 40,
                    height: 3,
                    borderRadius: 1.5,
                    backgroundColor: theme.palette.secondary.main
                  }
                }}
              >
                Organization Information
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    label="Organization Name"
                    name="organizationName"
                    value={formData.organizationName}
                    onChange={handleInputChange}
                    variant="outlined"
                    sx={{ mb: 3 }}
                  />

                  <FormControl fullWidth required sx={{ mb: 3 }}>
                    <InputLabel id="segment-label">Organization Segment</InputLabel>
                    <Select
                      labelId="segment-label"
                      id="segment"
                      name="segment"
                      value={formData.segment}
                      onChange={handleSelectChange}
                      label="Organization Segment"
                    >
                      {segments.map((segment) => (
                        <MenuItem key={segment} value={segment}>
                          {segment}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    required
                    fullWidth
                    label="Country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="Enter your country"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    label="Organization Brief"
                    name="organizationBrief"
                    value={formData.organizationBrief}
                    onChange={handleInputChange}
                    variant="outlined"
                    multiline
                    rows={9}
                    placeholder="Provide a brief description of your organization, its mission, and its activities in the healthcare or life sciences sector..."
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ArrowForwardIcon sx={{ transform: 'rotate(180deg)' }} />}
                  onClick={() => setActiveSection('personal')}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    px: 3,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Back to Personal Info
                </Button>

                <Button
                  variant="contained"
                  color="secondary"
                  endIcon={<ArrowForwardIcon />}
                  size="large"
                  onClick={() => setActiveSection('expertise')}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    px: 3,
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Continue to Expertise
                </Button>
              </Box>
            </Box>
          )}

          {/* Expertise & Sectors Section */}
          {activeSection === 'expertise' && (
            <Box>
              <Typography
                variant="h5"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  position: 'relative',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: 40,
                    height: 3,
                    borderRadius: 1.5,
                    backgroundColor: theme.palette.secondary.main
                  }
                }}
              >
                Expertise & Industry Sectors
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    mb: 1,
                    fontWeight: 500,
                    color: theme.palette.text.primary
                  }}
                >
                  Select Relevant Sectors
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 2,
                    color: theme.palette.text.secondary
                  }}
                >
                  Choose the sectors that align with your expertise and interests.
                </Typography>

                <FormControl fullWidth required>
                  <InputLabel id="sectors-label">Sectors</InputLabel>
                  <Select
                    labelId="sectors-label"
                    id="sectors"
                    multiple
                    value={formData.selectedSectors}
                    onChange={handleSectorChange}
                    input={<OutlinedInput label="Sectors" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip
                            key={value}
                            label={value}
                            size="small"
                            sx={{
                              borderRadius: 1,
                              backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                              color: theme.palette.secondary.main,
                              fontWeight: 500
                            }}
                          />
                        ))}
                      </Box>
                    )}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 300
                        }
                      }
                    }}
                  >
                    {sectors.map((sector) => (
                      <MenuItem key={sector} value={sector}>
                        <Checkbox
                          checked={formData.selectedSectors.indexOf(sector) > -1}
                          sx={{
                            '&.Mui-checked': {
                              color: theme.palette.secondary.main
                            }
                          }}
                        />
                        <ListItemText primary={sector} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Selected sectors visualization */}
              {formData.selectedSectors.length > 0 && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    borderRadius: 2,
                    mb: 4,
                    backgroundColor: alpha(theme.palette.background.paper, 0.6),
                    borderColor: alpha(theme.palette.primary.main, 0.2)
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Your Selected Sectors
                  </Typography>

                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {formData.selectedSectors.map((sector) => (
                      <Grid item key={sector}>
                        <Chip
                          label={sector}
                          sx={{
                            borderRadius: 2,
                            py: 2,
                            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                            color: theme.palette.secondary.main,
                            fontWeight: 500,
                            '&:hover': {
                              backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                            }
                          }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<ArrowForwardIcon sx={{ transform: 'rotate(180deg)' }} />}
                  onClick={() => setActiveSection('organization')}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    px: 3,
                    textTransform: 'none',
                    fontWeight: 500
                  }}
                >
                  Back to Organization
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveAltIcon />}
                  size="large"
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    py: 1,
                    px: 4,
                    textTransform: 'none',
                    fontWeight: 600,
                    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {loading ? 'Registering...' : 'Complete Registration'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Benefits section */}
      <Box sx={{ mt: 8, mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            mb: 4,
            fontWeight: 600,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 60,
            height: 4,
            borderRadius: 2,
            backgroundColor: theme.palette.secondary.main
          }
        }}
      >
        Benefits of Registration
      </Typography>

      <Grid container spacing={3}>
        {[
          {
            title: 'Industry Insights',
            description: 'Access specialized reports, market analyses, and industry forecasts for the healthcare and life sciences sector.'
          },
          {
            title: 'Networking',
            description: 'Connect with professionals, experts, and stakeholders across the healthcare and pharmaceutical value chain.'
          },
          {
            title: 'Investment Opportunities',
            description: 'Discover potential investment projects and business opportunities in Saudi Arabias growing healthcare sector.'
          },
          {
            title: 'Knowledge Exchange',
            description: 'Participate in forums, discussions, and knowledge sharing with industry leaders and innovators.'
          }
        ].map((benefit, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              elevation={2}
              sx={{
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 12px 20px -10px rgba(0,0,0,0.2)'
                },
                height: '100%',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box
                sx={{
                  height: 8,
                  width: '100%',
                  background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.light} 50%, transparent)`
                }}
              />

              <CardContent sx={{ p: 3, flexGrow: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: 1.5,
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  {benefit.title}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {benefit.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>

    {/* OTP Verification Dialog */}
    <Dialog
      open={showOTPDialog}
      onClose={() => {}}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: 2
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" fontWeight={600} align="center">
          Verify Your Email
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
          We've sent a 6-digit verification code to
        </Typography>
        <Typography variant="body1" fontWeight={600} align="center" color="primary">
          {otpEmail}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2, mb: 3 }}>
          <TextField
            fullWidth
            label="Verification Code"
            value={otp}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              setOtp(value);
            }}
            placeholder="Enter 6-digit code"
            inputProps={{
              maxLength: 6,
              style: { fontSize: '24px', letterSpacing: '8px', textAlign: 'center' }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />

          <Typography
            variant="caption"
            color="text.secondary"
            align="center"
            sx={{ display: 'block', mt: 2 }}
          >
            Code expires in {otpExpiry} minutes
          </Typography>
        </Box>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Didn't receive the code?
          </Typography>
          <Button
            onClick={handleResendOTP}
            disabled={resendCooldown > 0 || loading}
            sx={{ mt: 1, textTransform: 'none' }}
          >
            {resendCooldown > 0
              ? `Resend in ${resendCooldown}s`
              : 'Resend Code'}
          </Button>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={() => {
            setShowOTPDialog(false);
            setOtp('');
          }}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleVerifyOTP}
          variant="contained"
          color="secondary"
          disabled={loading || otp.length !== 6}
          endIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{
            textTransform: 'none',
            px: 4,
            borderRadius: 2
          }}
        >
          {loading ? 'Verifying...' : 'Verify Email'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Snackbar for notifications */}
    <Snackbar
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={() => setSnackbar({ ...snackbar, open: false })}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        severity={snackbar.severity}
        sx={{ width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  </Container>
);
};

export default RegisterPage;